import React from 'react';
import PropTypes from 'prop-types';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eb/social-login-facebook';

import { AlertChunky } from '@eb/eds-iconography';
import { Button } from '@eb/eds-button';
import { SIZE_BLOCK } from '@eb/eds-button';
import { withMainControls } from '@eb/eds-structure';
import { TYPE_ERROR } from '@eb/eds-notification';

import PageHeader from '../../components/PageHeader';
import LookUpForm from '../../components/forms/LookUpForm';
import SplitContainer from '../../connectedComponents/SplitContainer';

import { EXISTING_ACCOUNT_INFO } from '../../constants/messages';
import { APPLE_AUTHENTICATION_DATA_PROPTYPES } from '../../constants/proptypes';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/SOCIAL_LOOK_UP_PATH
 */
class SocialLookupPage extends React.Component {
    static propTypes = {
        socialAuthenticationData: PropTypes.shape({
            apple: APPLE_AUTHENTICATION_DATA_PROPTYPES.isRequired,
        }),
        onExistingAccountCheck: PropTypes.func.isRequired,
        trackOnLoadPage: PropTypes.func,
        trackOnYesButton: PropTypes.func,
        usesNewStyles: PropTypes.bool,
    };

    /**
     * Create account as it is non existent + login
     * Possible errors:
     * * Token > expired due to expirationTime
     * * * Show error `expired token` > Go back to step 1
     */

    componentDidMount() {
        const {
            trackOnLoadPage,
            socialAuthenticationData: {
                apple: { isPrivateEmail },
            },
        } = this.props;

        // this may not be triggering
        if (trackOnLoadPage) {
            trackOnLoadPage(isPrivateEmail);
        }
    }

    _handleExistingUserClick = () => {
        const { trackOnYesButton, onExistingAccountCheck } = this.props;

        onExistingAccountCheck({ userHasAccount: true });

        if (trackOnYesButton) {
            trackOnYesButton();
        }
    };

    _handleOnNewUserClick = () => {
        const {
            socialAuthenticationData: {
                apple: { accessToken, email },
            },
            linkAccountOnNewUser,
            onExistingAccountCheck,
        } = this.props;

        onExistingAccountCheck({ userHasAccount: false });

        linkAccountOnNewUser({
            externalProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
            externalToken: accessToken,
            email,
        });
    };

    _handleSubmit = (formData) => {
        const {
            socialAuthenticationData: {
                apple: { accessToken },
            },
            clearAuthNotification,
            onLookupUser,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // This will have to be updated once and if we enable fb and google through here.
        return onLookupUser(formData, {
            accessToken,
            sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
        });
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification();
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    render() {
        const { userHasAccount, usesNewStyles } = this.props;

        let component = (
            <div>
                <div
                    className="eds-l-mar-bot-4"
                    data-automation="social-login-existing-account"
                >
                    <Button
                        size={SIZE_BLOCK}
                        style="fill"
                        onClick={this._handleExistingUserClick}
                    >
                        {EXISTING_ACCOUNT_INFO.yesButtonText}
                    </Button>
                </div>
                <div
                    className="eds-l-mar-bot-4"
                    data-automation="social-login-new-account"
                >
                    <Button
                        size={SIZE_BLOCK}
                        style="neutral"
                        onClick={this._handleOnNewUserClick}
                    >
                        {EXISTING_ACCOUNT_INFO.noButtonText}
                    </Button>
                </div>
            </div>
        );

        if (userHasAccount) {
            component = (
                <LookUpForm
                    buttonCTA="Next"
                    shouldAnimate={true}
                    onSubmit={this._handleSubmit}
                    hideSocialLogin={true}
                    showNotification={this._handleShowNotification}
                    clearNotification={this._handleClearNotification}
                />
            );
        }

        const header = (
            <PageHeader
                headerText={EXISTING_ACCOUNT_INFO.headerText}
                subtitleText={EXISTING_ACCOUNT_INFO.subtitleText}
                usesNewStyles={usesNewStyles}
            />
        );

        return <SplitContainer header={header}>{component}</SplitContainer>;
    }
}

export default withMainControls(SocialLookupPage);
